import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, Download, Mail, Phone, Inbox, Users } from "lucide-react";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BulkEmailModal from "@/components/BulkEmailModal";

const AUD = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
const LAB_LABEL = {
  pending_sample: "Awaiting sample", awaiting_drop_off: "Awaiting drop-off",
  received: "Received", results_entered: "Results entered",
  submitted_for_review: "In review", in_review: "In review",
  published: "Published", failed: "Failed",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}
function fmtSpend(cents) {
  if (!cents || Number.isNaN(cents)) return "—";
  return AUD.format(cents / 100);
}

export default function AdminPartnerCustomers() {
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [emailOpen, setEmailOpen] = useState(false);

  // Load partner directory once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/partners", { params: { active: true } });
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
        // Alphabetical by business name for the dropdown.
        list.sort((a, b) => (a.business_name || "").localeCompare(b.business_name || ""));
        setPartners(list);
        if (list.length && !partnerId) setPartnerId(list[0].id);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load partners");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  // Load customers whenever partner or search changes.
  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { as_partner_id: partnerId };
        if (debouncedQ) params.q = debouncedQ;
        const r = await api.get("/partner/customers", { params });
        if (cancelled) return;
        setRows(r.data?.rows || []);
        setError("");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Failed to load customers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId, debouncedQ]);

  const totalSpend = useMemo(
    () => rows.reduce((n, r) => n + (r.lifetime_spend_cents || 0), 0),
    [rows],
  );

  // Reset selection whenever the partner or filter changes.
  useEffect(() => { setSelected(new Set()); }, [partnerId, debouncedQ]);

  const isEligible = (r) => Boolean(r.email);
  const eligibleKeys = useMemo(
    () => rows.filter(isEligible).map((r) => r.customer_key),
    [rows],
  );
  const allEligibleSelected = eligibleKeys.length > 0 &&
    eligibleKeys.every((k) => selected.has(k));
  const toggleRow = (r) => {
    if (!isEligible(r)) return;
    const next = new Set(selected);
    if (next.has(r.customer_key)) next.delete(r.customer_key);
    else next.add(r.customer_key);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allEligibleSelected) setSelected(new Set());
    else setSelected(new Set(eligibleKeys));
  };

  const partnerLabel = useMemo(() => {
    const p = partners.find((x) => x.id === partnerId);
    return p ? (p.business_name || p.partner_slug || partnerId) : partnerId;
  }, [partners, partnerId]);

  const downloadCsv = async () => {
    if (!partnerId) return;
    setExporting(true);
    try {
      const params = { as_partner_id: partnerId };
      if (debouncedQ) params.q = debouncedQ;
      const r = await api.get("/partner/customers.csv", { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(partnerLabel || "partner").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export CSV");
    } finally { setExporting(false); }
  };

  return (
    <PortalShell title="Partner Customers" subtitle="Admin" wide>
      <div className="space-y-6" data-testid="admin-partner-customers-page">

        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-700"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Partners
        </Link>

        {/* Partner picker + search + export */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
          <div className="flex items-center gap-2 min-w-[280px]">
            <Users className="w-4 h-4 text-slate-500" />
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger
                data-testid="admin-partner-customers-partner-select"
                className="h-10 w-full rounded-xl"
              >
                <SelectValue placeholder="Select a partner" />
              </SelectTrigger>
              <SelectContent>
                {partners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.business_name || p.partner_slug || p.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              data-testid="admin-partner-customers-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, phone or suburb…"
              className="w-full pl-9 pr-3 h-10 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
            />
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-slate-500">
              {rows.length} customer{rows.length === 1 ? "" : "s"} · {fmtSpend(totalSpend)}
            </span>
            <button
              data-testid="admin-partner-customers-email-selected"
              onClick={() => setEmailOpen(true)}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 text-white text-xs font-semibold px-3 h-10 hover:bg-emerald-700 disabled:opacity-40"
            >
              <Mail className="w-3.5 h-3.5" />
              Email selected ({selected.size})
            </button>
            <button
              data-testid="admin-partner-customers-export-csv"
              onClick={downloadCsv}
              disabled={exporting || !rows.length}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 text-white text-xs font-semibold px-3 h-10 hover:bg-slate-800 disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        {error && (
          <div data-testid="admin-partner-customers-error" className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 text-sm">
            Loading customers…
          </div>
        )}

        {!loading && !rows.length && !error && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Inbox className="w-10 h-10 mx-auto text-slate-400" />
            <div className="mt-4 text-slate-700 font-medium">No customers for {partnerLabel}</div>
            <div className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
              As bookings are attributed to this partner, unique customers will appear here.
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-3 px-3 w-8">
                      <input
                        type="checkbox"
                        data-testid="admin-partner-customers-select-all"
                        checked={allEligibleSelected}
                        onChange={toggleAll}
                        disabled={eligibleKeys.length === 0}
                        aria-label="Select all eligible customers"
                        className="w-4 h-4 accent-emerald-600 disabled:opacity-40"
                      />
                    </th>
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-left py-3 px-4">Contact</th>
                    <th className="text-right py-3 px-4">Bookings</th>
                    <th className="text-right py-3 px-4">Properties</th>
                    <th className="text-right py-3 px-4">Lifetime spend</th>
                    <th className="text-left py-3 px-4">Last booked</th>
                    <th className="text-left py-3 px-4">Latest lab</th>
                    <th className="py-3 pr-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => {
                    const eligible = isEligible(r);
                    return (
                    <tr key={r.customer_key} className="hover:bg-slate-50/60" data-testid={`admin-partner-customer-row-${r.customer_key}`}>
                      <td className="py-3 px-3 align-top">
                        <input
                          type="checkbox"
                          data-testid={`admin-partner-customer-select-${r.customer_key}`}
                          checked={selected.has(r.customer_key)}
                          onChange={() => toggleRow(r)}
                          disabled={!eligible}
                          title={eligible ? "" : "No email address on file — can't include in campaigns"}
                          className="w-4 h-4 accent-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="py-3 px-4 align-top">
                        <div className="font-semibold text-slate-900">{r.name || "—"}</div>
                        <div className="text-xs text-slate-500">{r.suburb || "—"}</div>
                      </td>
                      <td className="py-3 px-4 align-top">
                        {r.email && (
                          <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-cyan-700">
                            <Mail className="w-3.5 h-3.5" /> {r.email}
                          </a>
                        )}
                        {r.phone && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-500">
                            <Phone className="w-3 h-3" /> {r.phone}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums">{r.bookings_count}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{r.properties_count}</td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold">{fmtSpend(r.lifetime_spend_cents)}</td>
                      <td className="py-3 px-4 text-slate-600">{fmtDate(r.last_booked_at)}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                          {LAB_LABEL[r.latest_lab_status] || r.latest_lab_status || "—"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <Link
                          to={`/admin/partners/customers/${encodeURIComponent(partnerId)}/${encodeURIComponent(r.customer_key)}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white text-xs font-semibold px-2.5 py-1.5 hover:bg-slate-800"
                        >
                          View <ArrowRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {emailOpen && selected.size > 0 && partnerId && (
        <BulkEmailModal
          customerKeys={Array.from(selected)}
          asPartnerId={partnerId}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </PortalShell>
  );
}
